import { AlertCircle, ArrowLeft, Loader2, Pencil, ShieldCheck } from 'lucide-react';
import { BOOKING_AREAS } from '@/data/contact';
import { dublinDayLabel } from './DateTimeStep';

const Row = ({ label, value, onEdit, testid }) => (
  <div className="flex items-start justify-between gap-4 py-3" data-testid={testid}>
    <div>
      <dt className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">{label}</dt>
      <dd className="mt-1 text-sm font-bold text-navy-900">{value}</dd>
    </div>
    {onEdit && (
      <button
        type="button"
        onClick={onEdit}
        aria-label={`Edit ${label}`}
        className="inline-flex items-center gap-1 text-xs font-bold text-gold-700 underline-offset-2 transition-colors hover:text-gold-700 hover:underline"
      >
        <Pencil className="h-3 w-3" aria-hidden="true" /> Edit
      </button>
    )}
  </div>
);

// Step 4 — review before confirming. Editing jumps back without losing the
// selected appointment. No invented cancellation/refund terms — only the
// genuine secure-link statement.
export const ReviewStep = ({ meetingType, slot, details, onEdit, onBack, onConfirm, submitting, conflict, submitError }) => {
  const areaLabel = BOOKING_AREAS.find((a) => a.value === details.areaOfInterest)?.label || details.areaOfInterest;

  return (
    <div data-testid="booking-step-review">
      <h3 className="text-xl font-extrabold tracking-tight text-navy-900">Review your booking</h3>

      <dl className="mt-6 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white px-6">
        <Row label="Meeting type" value={meetingType.name} onEdit={() => onEdit(1)} testid="review-type" />
        <Row label="Date" value={dublinDayLabel(slot.dayKey)} onEdit={() => onEdit(2)} testid="review-date" />
        <Row
          label="Time"
          value={`${slot.time} – ${slot.endTime}`}
          onEdit={() => onEdit(2)}
          testid="review-time"
        />
        <Row label="Timezone" value="Irish time (Europe/Dublin)" testid="review-tz" />
        <Row label="Meeting format" value="Online (video call)" testid="review-format" />
        <Row label="Name" value={details.name} onEdit={() => onEdit(3)} testid="review-name" />
        <Row label="Business" value={details.businessName} onEdit={() => onEdit(3)} testid="review-business" />
        <Row label="Email" value={details.email} onEdit={() => onEdit(3)} testid="review-email" />
        {details.phone && (
          <Row label="Phone" value={details.phone} onEdit={() => onEdit(3)} testid="review-phone" />
        )}
        <Row label="Area of interest" value={areaLabel} onEdit={() => onEdit(3)} testid="review-area" />
      </dl>

      <p className="mt-5 flex items-start gap-2.5 rounded-lg border border-slate-200 bg-mist p-4 text-sm leading-relaxed text-slate-600">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-navy-900" aria-hidden="true" />
        You can cancel or reschedule your appointment at any time using the secure links shown with
        your booking confirmation.
      </p>

      {conflict && (
        <p role="alert" data-testid="booking-conflict" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-red-700">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          That appointment time is no longer available. Please choose another time.
        </p>
      )}

      {submitError && (
        <p role="alert" data-testid="booking-submit-error" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-red-700">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          {submitError}
        </p>
      )}

      <div className="mt-8 flex items-center justify-between">
        <button type="button" onClick={onBack} data-testid="booking-back-review" className="inline-flex items-center gap-2 text-sm font-bold text-navy-900 underline-offset-2 hover:underline">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={submitting}
          data-testid="booking-confirm-btn"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-gold-600 px-7 py-3.5 text-sm font-bold text-navy-950 transition-colors hover:bg-gold-500 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-200 disabled:text-slate-500"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {submitting ? 'Confirming…' : 'Confirm Appointment'}
        </button>
      </div>
    </div>
  );
};
